import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Paper,
    Tabs,
    Tab,
    Divider,
    useTheme,
    useMediaQuery,
    Container,
    Drawer,
    IconButton,
    alpha,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Breadcrumbs,
    Link
} from "@mui/material";
import {
    AccountCircleOutlined,
    LockOutlined,
    MenuOpen,
    Menu as MenuIcon,
    Home
} from "@mui/icons-material";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import AccountDetails from "./settingsTabs/AccountDetails";
import Security from "./settingsTabs/Security";

// TabPanel component for accessibility
function TabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`settings-tabpanel-${index}`}
            aria-labelledby={`settings-tab-${index}`}
            {...other}
            style={{ width: '100%' }}
        >
            {value === index && (
                <Box sx={{ py: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

// Tab accessibility properties
function a11yProps(index) {
    return {
        id: `settings-tab-${index}`,
        'aria-controls': `settings-tabpanel-${index}`,
    };
}

export default function Settings(props) {
    const [activePage, setActivePage] = useState(0);
    const [loading, setLoading] = useState(true);
    const [mobileOpen, setMobileOpen] = useState(false);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // Handle page change
    const handlePageChange = (event, newValue) => {
        setLoading(true);
        setActivePage(newValue);
        setMobileOpen(false);
        // Simulate loading for a smoother transition feel
        setTimeout(() => {
            setLoading(false);
        }, 300);
    };

    // Toggle mobile drawer
    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    useEffect(() => {
        props.refreshSideBar();
        // Short timeout to make loading animation visible for better UX
        setTimeout(() => {
            setLoading(false);
        }, 500);
    }, []);

    // Sidebar content - shared between mobile drawer and desktop sidebar
    const sidebarContent = (
        <Box sx={{ p: 2, height: '100%' }}>
            <Typography
                variant="h5"
                sx={{
                    fontWeight: 600,
                    mb: 3,
                    color: 'text.primary',
                    pl: 1
                }}
            >
                Settings
            </Typography>

            <List component="nav" sx={{ width: '100%' }}>
                <ListItem
                    button
                    onClick={(e) => handlePageChange(e, 0)}
                    selected={activePage === 0}
                    sx={{
                        borderRadius: 1,
                        mb: 1,
                        '&.Mui-selected': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main,
                            '&:hover': {
                                backgroundColor: alpha(theme.palette.primary.main, 0.15),
                            }
                        },
                        '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.05),
                        }
                    }}
                >
                    <ListItemIcon
                        sx={{
                            color: activePage === 0 ? theme.palette.primary.main : 'text.secondary',
                            minWidth: '40px'
                        }}
                    >
                        <AccountCircleOutlined />
                    </ListItemIcon>
                    <ListItemText
                        primary="Account Details"
                        primaryTypographyProps={{
                            fontWeight: activePage === 0 ? 600 : 400,
                            color: activePage === 0 ? theme.palette.primary.main : 'text.primary'
                        }}
                    />
                </ListItem>

                <ListItem
                    button
                    onClick={(e) => handlePageChange(e, 1)}
                    selected={activePage === 1}
                    sx={{
                        borderRadius: 1,
                        mb: 1,
                        '&.Mui-selected': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main,
                            '&:hover': {
                                backgroundColor: alpha(theme.palette.primary.main, 0.15),
                            }
                        },
                        '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.05),
                        }
                    }}
                >
                    <ListItemIcon
                        sx={{
                            color: activePage === 1 ? theme.palette.primary.main : 'text.secondary',
                            minWidth: '40px'
                        }}
                    >
                        <LockOutlined />
                    </ListItemIcon>
                    <ListItemText
                        primary="Security"
                        primaryTypographyProps={{
                            fontWeight: activePage === 1 ? 600 : 400,
                            color: activePage === 1 ? theme.palette.primary.main : 'text.primary'
                        }}
                    />
                </ListItem>
            </List>
        </Box>
    );

    return (
        <Container maxWidth="xl" sx={{ p: { xs: 0, sm: 2 } }}>
            {/* Loading Screen */}
            <Backdrop
                open={loading}
                sx={{
                    color: '#fff',
                    zIndex: (theme) => theme.zIndex.drawer + 1
                }}
            >
                <CircularProgress color="inherit" />
            </Backdrop>

            {/* Page header with breadcrumbs and mobile menu */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 3,
                    mt: { xs: 1, sm: 0 },
                    mx: { xs: 2, sm: 0 }
                }}
            >
                <Breadcrumbs aria-label="breadcrumb">
                    <Link
                        color="inherit"
                        href="/home"
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            textDecoration: 'none',
                            '&:hover': { textDecoration: 'underline' }
                        }}
                    >
                        <Home sx={{ mr: 0.5, fontSize: '0.9rem' }} fontSize="small" />
                        Dashboard
                    </Link>
                    <Typography color="text.primary" sx={{ fontWeight: 500 }}>
                        Settings
                    </Typography>
                </Breadcrumbs>

                {isMobile && (
                    <IconButton
                        color="inherit"
                        aria-label="open settings menu"
                        edge="end"
                        onClick={handleDrawerToggle}
                        sx={{ display: { md: 'none' } }}
                    >
                        {mobileOpen ? <MenuOpen /> : <MenuIcon />}
                    </IconButton>
                )}
            </Box>

            {/* Main content layout */}
            <Paper
                elevation={0}
                sx={{
                    display: 'flex',
                    borderRadius: 2,
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'divider',
                    height: { md: 'calc(100vh - 200px)', xs: 'auto' },
                    minHeight: '500px',
                    flexDirection: { xs: 'column', md: 'row' }
                }}
            >
                {/* Mobile drawer for settings navigation */}
                {isMobile && (
                    <Drawer
                        variant="temporary"
                        open={mobileOpen}
                        onClose={handleDrawerToggle}
                        ModalProps={{ keepMounted: true }}
                        sx={{
                            display: { xs: 'block', md: 'none' },
                            '& .MuiDrawer-paper': {
                                width: 280,
                                boxSizing: 'border-box',
                                borderRight: '1px solid',
                                borderColor: 'divider'
                            },
                        }}
                    >
                        {sidebarContent}
                    </Drawer>
                )}

                {/* Desktop sidebar */}
                <Box
                    sx={{
                        width: 280,
                        flexShrink: 0,
                        borderRight: '1px solid',
                        borderColor: 'divider',
                        display: { xs: 'none', md: 'block' },
                        bgcolor: 'background.paper',
                        height: '100%'
                    }}
                >
                    {sidebarContent}
                </Box>

                {/* Mobile tabs - visible only on mobile */}
                <Box sx={{ display: { xs: 'block', md: 'none' }, width: '100%' }}>
                    <Tabs
                        value={activePage}
                        onChange={handlePageChange}
                        variant="fullWidth"
                        sx={{
                            borderBottom: 1,
                            borderColor: 'divider',
                            bgcolor: alpha(theme.palette.primary.main, 0.03)
                        }}
                    >
                        <Tab
                            icon={<AccountCircleOutlined />}
                            label="Account"
                            {...a11yProps(0)}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 500,
                                py: 1.5
                            }}
                        />
                        <Tab
                            icon={<LockOutlined />}
                            label="Security"
                            {...a11yProps(1)}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 500,
                                py: 1.5
                            }}
                        />
                    </Tabs>
                </Box>

                {/* Content area */}
                <Box
                    sx={{
                        flex: 1,
                        p: { xs: 2, sm: 3 },
                        overflow: 'auto',
                        bgcolor: 'background.default',
                        display: 'flex'
                    }}
                >
                    <TabPanel value={activePage} index={0} sx={{ width: '100%' }}>
                        <Typography
                            variant="h5"
                            sx={{
                                mb: 3,
                                fontWeight: 600,
                                display: { xs: 'none', md: 'block' }
                            }}
                        >
                            Account Details
                        </Typography>
                        <Divider sx={{ mb: 3, display: { xs: 'none', md: 'block' } }} />
                        <AccountDetails snackBar={props.snackBar} />
                    </TabPanel>

                    <TabPanel value={activePage} index={1} sx={{ width: '100%' }}>
                        <Typography
                            variant="h5"
                            sx={{
                                mb: 3,
                                fontWeight: 600,
                                display: { xs: 'none', md: 'block' }
                            }}
                        >
                            Security
                        </Typography>
                        <Divider sx={{ mb: 3, display: { xs: 'none', md: 'block' } }} />
                        <Security snackBar={props.snackBar} />
                    </TabPanel>
                </Box>
            </Paper>
        </Container>
    );
}